/* eslint-disable @typescript-eslint/no-unused-vars */
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Drawer, Skeleton, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import _ from "lodash";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDialogs } from "../../../components";
import { FormProvider, FormState } from "../../../components/FormProvider";
import { CommunicationRecipient } from "../../../models";
import { useDeleteCommunicationRecipientMutation, useGetFamilyByIdQuery, useSaveCommunicationRecipientMutation } from "../../../redux/apiStore";
import { formatPhoneNumber } from "../../../utils/string";
import { EditContactForm } from "../contacts/detail/EditContactForm";

export function ContactsTable(props: { familyId: number }) {
  const { familyId } = props;
  const family = useGetFamilyByIdQuery({ id: familyId });
  const navigate = useNavigate();
  const theme = useTheme();
  const orderedFilteredData = _.orderBy(family.data?.communicationRecipients, [(x) => x.name], ["asc"]);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [editing, setEditing] = useState<boolean>(false);
  const formDataGetterRef = useRef<(() => CommunicationRecipient | null) | null>(null);
  const resetFormRef = useRef<(() => void) | null>(null);
  const [saveContact, saveContactStatus] = useSaveCommunicationRecipientMutation();
  const [deleteContact, deleteContactStatus] = useDeleteCommunicationRecipientMutation();
  const dialogs = useDialogs();
  const [selectedContact, setSelectedContact] = useState<CommunicationRecipient | null>(null);

  const handleSelect = (contact: CommunicationRecipient) => {
    setSelectedContact(contact);
    setEditing(true);
  };

  const handleSave = async (form: FormState) => {
    const contact = formDataGetterRef.current ? formDataGetterRef.current() : null;
    form.dismissAlert();
    if (!contact) {
      form.notifyError("Failed to save contact. Contact is null for some reason");
      return;
    }
    try {
      const result = await saveContact(contact).unwrap();
      form.notifySuccess("Contact saved");
      setEditing(false);
      setSelectedContact(null);
    } catch (err) {
      form.notifyError("Error saving contact", err);
    }
  };

  const handleDelete = async (form: FormState) => {
    const contact = formDataGetterRef.current ? formDataGetterRef.current() : null;
    form.dismissAlert();
    if (!contact) {
      form.notifyError("Failed to delete contact. Contact is null for some reason");
      return;
    }
    try {
      const confirmed = await dialogs.confirm(`Are you sure you want to delete ${contact.name}?`);
      if (confirmed) {
        const result = await deleteContact({ id: contact.id }).unwrap();
        form.notifySuccess("Contact deleted");
        setEditing(false);
        setSelectedContact(null);
      } else {
        console.log("Contact not deleted");
      }
    } catch (err) {
      form.notifyError("Error deleting contact", err);
    }
  };

  const handleCancel = async (form: FormState) => {
    try {
      if (resetFormRef.current) {
        resetFormRef.current();
        setEditing(false);
        setSelectedContact(null);
      } else {
        console.error("Failed to undo form changes. No form reset method has been set.");
      }
    } catch (err) {
      form.notifyError("Error undoing changes", err);
    }
  };

  const loadingRow = (
    <TableRow>
      <TableCell>
        <Skeleton width={120} height={30} />
      </TableCell>
      <TableCell>
        <Skeleton width={150} height={30} />
      </TableCell>
      <TableCell>
        <Skeleton width={200} height={30} />
      </TableCell>
      {!isSmallScreen && <TableCell align="center"></TableCell>}
      {!isSmallScreen && <TableCell align="center"></TableCell>}
    </TableRow>
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={editing}
        sx={{ zIndex: 1550 }}
        slotProps={{
          paper: {
            sx: {
              width: isSmallScreen ? "100%" : "400px", // 100% width on mobile
              zIndex: 1550,
            },
          },
        }}
      >
        <Stack sx={{ p: 2, height: "100%" }}>
          <FormProvider fullHeight onSave={handleSave} onCancel={handleCancel} onDelete={handleDelete} showControls={false}>
            <EditContactForm familyId={familyId} contact={selectedContact} dataGetterRef={formDataGetterRef} resetFormRef={resetFormRef} />
          </FormProvider>
        </Stack>
      </Drawer>
      <TableContainer component={Paper}>
        <Stack direction="row" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ m: 0 }}>
            Contacts
          </Typography>
          <Box flexGrow={1} />
          <Button variant="outlined" onClick={() => setEditing(true)}>
            Add
          </Button>
        </Stack>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Email</TableCell>
              {!isSmallScreen && <TableCell align="center">Email Opted In</TableCell>}
              {!isSmallScreen && <TableCell align="center">SMS Opted In</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {family.isFetching && [loadingRow, loadingRow]}
            {orderedFilteredData?.map((contact) => {
              return (
                <TableRow key={contact.id} sx={{ cursor: "pointer" }} hover onClick={() => handleSelect(contact)}>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell>{formatPhoneNumber(contact.phoneNumber)}</TableCell>
                  <TableCell>{contact.email}</TableCell>
                  {!isSmallScreen && <TableCell align="center">{contact.isEmailOptedIn && <FontAwesomeIcon icon={faCheck} />}</TableCell>}
                  {!isSmallScreen && <TableCell align="center">{contact.isSMSOptedIn && <FontAwesomeIcon icon={faCheck} />}</TableCell>}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
